import { Model } from '@vuex-orm/core'
import type { Request } from '@vuex-orm/plugin-axios'
import AppState from '@/utils/app.state'
import TotalPrice from '@/models/TotalPrice'
import { getCode } from '@/lang'

export interface LangedExternalProductDescription {
  friendlyName?: string
  description?: string
  url?: string
}

export default class ExternalProduct extends Model {
  declare id: number
  declare bookingId: number
  declare productCode: string
  declare pricingType: number
  declare pricingValue: number
  declare amount: number
  declare enabled: boolean
  declare langedDescriptions: Record<string, LangedExternalProductDescription>
  declare active: boolean
  declare vat: number

  static entity = 'externalProduct'

  static fields() {
    return {
      id: this.attr(null),
      bookingId: this.attr(1),
      productCode: this.string(null),
      pricingType: this.number(0),
      pricingValue: this.number(0),
      amount: this.number(0),
      enabled: this.boolean(false),
      langedDescriptions: this.attr({}),
      active: this.boolean(false),
      vat: this.number(0),
    }
  }

  get netValue() {
    /* Net value is fixed */
    if (this.pricingType === 1) {
      return this.pricingValue
    }

    /* Get quote data */
    const quote = TotalPrice.query().get()
    if (!quote || !quote.length) return 0

    /* Booking total */
    const roomConfigurationsTotal = quote.reduce((sum, item) => sum + item.totalPrice, 0)

    /* Net value is percentage of booking total */
    return this.pricingValue / 100 * roomConfigurationsTotal
  }

  get totalValue() {
    return this.netValue + this.vatValue
  }

  get vatValue() {
    return this.vat / 100 * this.netValue
  }

  get langedDescription(): LangedExternalProductDescription | undefined {
    const shortLocale = getCode()
    return this.langedDescriptions && shortLocale
      ? this.langedDescriptions[shortLocale.toUpperCase()]
      : undefined
  }

  get langedName() {
    const desc = this.langedDescription
    return desc && desc.friendlyName
  }

  get langedUrl() {
    const desc = this.langedDescription
    return desc && desc.url
  }

  static get objectBookingFeeProduct() {
    return this.query()
      .where((item: ExternalProduct) => item.productCode.toLowerCase() === 'booking_fee')
      .first()
  }

  static get partialPaymentFeeProduct() {
    return this.query()
      .where((item: ExternalProduct) => item.productCode.toLowerCase() === 'partial_payment')
      .first()
  }

  static updateObjectBookingFeeProduct(data: Partial<ExternalProduct>) {
    const objectBookingFeeProduct = this.objectBookingFeeProduct
    if (!objectBookingFeeProduct) return Promise.resolve(false)

    /* Calculate new active state */
    if (typeof data.amount === 'number') {
      data.active = !!data.amount
    }

    return this.update({
      where: objectBookingFeeProduct.$id as string,
      data
    })
  }

  static get objectBookingFee() {
    const feeProduct = this.query()
      .where((item: ExternalProduct) => item.productCode.toLowerCase() === 'booking_fee')
      .first()

    return feeProduct ? feeProduct.totalValue : null
  }

  static apiConfig = {
    actions: {
      /* get prices, other data fetched in basic property call (accommodation instead of content API to get prices). */
      fetch(this: Request, checkin: string) {
        // return this.get(`/accommodation/v1/properties/${AppState.currentProperty}/externalproducts/minimal`)
        return this.get(`/accommodation/v1/properties/${AppState.currentProperty}/externalproducts?getEnabledOnly=true&checkindate=${checkin}`)
      }
    }
  }
}
